import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from '../../i18n';
import type { DashboardAsyncReportLabelI18nKey } from './dashboardAsyncReportLabelI18nKeys';
import { DELIVERY_DOWNLOAD_FROM_ASYNC_DASHBOARD_STATE_KEY } from '../../constants/deliveryDownloadNavigation';

export interface AsyncReportDownloadModalProps {
    visible: boolean;
    onClose: () => void;
    fileName: string;
    /** 报表类型展示名对应的 i18n key，须为运营看板预置 key（见 dashboardAsyncReportLabelI18nKeys） */
    reportLabelI18nKey: DashboardAsyncReportLabelI18nKey;
    /** 是否显示"跳转到下载页面"按钮，默认 true；由调用方按自身权限判断传入，本组件不做权限判断 */
    showGoToDownloadPage?: boolean;
}

/**
 * 异步报表生成后的通用提示弹窗（与包裹列表 ParcelsDownloadModal 文案分离）。
 */
const AsyncReportDownloadModal: React.FC<AsyncReportDownloadModalProps> = ({
    visible,
    onClose,
    fileName,
    reportLabelI18nKey,
    showGoToDownloadPage = true,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [copyLoading, setCopyLoading] = useState(false);

    const reportLabel = t(reportLabelI18nKey);

    const handleCopyFileName = async () => {
        setCopyLoading(true);
        try {
            await navigator.clipboard.writeText(fileName);
            message.success(t('delivery.parcels.message.downloadCopySuccess'));
        } catch (error) {
            message.error(t('delivery.parcels.message.downloadCopyFailed'));
        } finally {
            setCopyLoading(false);
        }
    };

    const handleGoToDownloadPage = () => {
        const path = location.pathname;
        if (path.includes('/qualitycontrol')) {
            navigate('/qualitycontrol/download');
        } else if (path.startsWith('/partner-control')) {
            navigate('/partner-control/download');
        } else if (path.startsWith('/delivery')) {
            navigate('/delivery/download', {
                state: { [DELIVERY_DOWNLOAD_FROM_ASYNC_DASHBOARD_STATE_KEY]: true },
            });
        } else if (path.startsWith('/wms')) {
            navigate('/wms/download');
        } else if (path.startsWith('/dashboard')) {
            navigate('/dashboard/download');
        } else {
            navigate('/central/download');
        }
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-purple-500 mr-3"></div>
                    <span className="text-lg font-medium">{t('components.asyncReportDownload.title')}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={600}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('components.button.close')}
                </Button>,
                <Button key="copy" icon={<CopyOutlined />} loading={copyLoading} onClick={handleCopyFileName}>
                    {t('components.button.copyFilename')}
                </Button>,
                ...(showGoToDownloadPage ? [
                    <Button key="download" type="primary" onClick={handleGoToDownloadPage}>
                        {t('components.button.goToDownloadPage')}
                    </Button>,
                ] : []),
            ]}
        >
            <div className="py-4">
                <p className="text-gray-800 font-medium mb-4">
                    {t('components.asyncReportDownload.description', { reportLabel })}
                </p>
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <span className="font-medium">{t('components.asyncReportDownload.filenameLabel')}: </span>
                    <code className="break-all">{fileName}</code>
                </div>
            </div>
        </Modal>
    );
};

export default AsyncReportDownloadModal;
